import { CloseButton, VisuallyHidden } from '@chakra-ui/react'
import { Box, BoxProps, HStack, Input, Text, VStack } from '@chakra-ui/react'
import { Tag } from '@chakra-ui/react'
import { X } from 'lucide-react'
import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TagVariant } from '~/components/defaults/componentStyles/Tag.style'
import { useTagInputStyles } from '~/components/shared/componentStyles/TagInput.style'
import { useAnnounce } from '~/components/shared/LiveAnnouncer'
import { tryGetValueOrDefault } from '~/utils/common'

type TagInputProps = BoxProps & {
  /** Visible label for the input. It stays the input's name when it is disabled at `maxTags`. */
  label?: string
  placeholder?: string
  value?: string
  allowMultiple?: boolean
  guideText?: string
  evalOnBlur?: boolean
  maxTags?: number
  /** Localized noun for one value, used in the invalid-value message (e.g. "email"). */
  valueType?: string
  initialTags?: string[]
  onInputValueChange?: (value: string) => void
  valueValidator?: (value: string) => boolean
  onTagsUpdated?: (tags: string[]) => void
}

export const TagInput = React.forwardRef<HTMLDivElement, TagInputProps>(
  (props: TagInputProps, ref) => {
    const { t } = useTranslation()
    const announce = useAnnounce()
    const [tags, setTags] = useState<string[]>([])
    const [inputValue, setInputValue] = useState<string>('')
    const [inputErrors, setInputErrors] = useState<string[]>()
    const inputRef = useRef<HTMLInputElement>(null)
    const focusInputAfterRender = useRef(false)
    const focusLastTagAfterRender = useRef(false)
    const tagContainerRef = useRef<HTMLDivElement>(null)
    const styles = useTagInputStyles()
    const idBase = useId()
    const inputId = `${idBase}-input`
    const guideTextId = `${idBase}-guide`
    const errorId = `${idBase}-error`

    const {
      label,
      placeholder,
      value: externalValue = '',
      allowMultiple = true,
      guideText,
      evalOnBlur = true,
      maxTags = 5,
      valueType,
      initialTags = [],
      onInputValueChange,
      valueValidator,
      onTagsUpdated,
      ...rest
    } = props

    const updateTagState = (newTags: string[]) => {
      setTags(newTags)
      onTagsUpdated?.(newTags)
    }

    const handleAddNewTags = (newTags: string[]) => {
      const errorValues: string[] = []
      const filteredTags = newTags
        .map((tag) => tag.trim())
        .filter((tag) => {
          const isValid = valueValidator ? valueValidator(tag) : true

          if (!isValid) {
            errorValues.push(tag)
          }

          return isValid && !tags.includes(tag)
        })
      const mergedTags = [...tags, ...filteredTags].slice(0, maxTags)
      updateTagState(mergedTags)
      return { errorValues, mergedTags }
    }

    useEffect(() => {
      handleAddNewTags(initialTags)
    }, [])

    useEffect(() => {
      if (focusLastTagAfterRender.current) {
        focusLastTagAfterRender.current = false
        const removeButtons =
          tagContainerRef.current?.querySelectorAll<HTMLButtonElement>('button')
        removeButtons?.[removeButtons.length - 1]?.focus()
        return
      }
      if (!focusInputAfterRender.current) return
      focusInputAfterRender.current = false
      inputRef.current?.focus()
    }, [tags])

    const getErrorMessage = (errors: string[]) => {
      const isPlural = errors.length > 1

      if (!valueType) {
        return isPlural
          ? t('tagInput.valuesNotAdded', 'These values could not be added')
          : t('tagInput.valueNotAdded', 'This value could not be added')
      }

      return isPlural
        ? t(
            'tagInput.invalidValues',
            'These values are not a valid {{valueType}}',
            {
              valueType,
            },
          )
        : t(
            'tagInput.invalidValue',
            'This value is not a valid {{valueType}}',
            {
              valueType,
            },
          )
    }

    const handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
      e?.preventDefault()
      const value = inputRef.current?.value
      if (!value) return
      const newTags = value.split(',')
      const inputHadFocus = document.activeElement === inputRef.current
      const { errorValues: errors, mergedTags } = handleAddNewTags(newTags)
      const reachedLimit = tags.length < maxTags && mergedTags.length >= maxTags
      // The input leaves the tab order at maxTags, so move focus to the tag
      // that was just added instead of leaving it on an unavailable field.
      if (inputHadFocus && reachedLimit) {
        focusLastTagAfterRender.current = true
      }
      if (errors.length > 0) {
        setInputErrors(errors)
      }
      const error = errors.length > 0 ? getErrorMessage(errors) : null
      const limit = reachedLimit
        ? t(
            'tagInput.limitReached',
            'Maximum of {{maxTags}} reached. Remove one to add another.',
            { maxTags },
          )
        : null
      const message =
        error && limit
          ? t('tagInput.errorAndLimit', '{{error}}. {{limit}}', {
              error,
              limit,
            })
          : (error ?? limit)
      if (message) announce(message)

      inputRef.current!.value = errors.join(',')
      setInputValue('')
    }

    const handleFocusLost = () => {
      if (!evalOnBlur) return

      handleSubmit()
    }

    const handleDeleteTag = (index: number) => () => {
      const newTags = [...tags]
      newTags.splice(index, 1)
      focusInputAfterRender.current = true
      updateTagState(newTags)
    }

    const handleResetError = () => {
      if (inputErrors) {
        setInputErrors(undefined)
      }
    }

    const handleInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
      onInputValueChange?.(e.target.value)
      handleResetError()
    }

    // Holding Enter on the last allowed address keeps repeating after focus
    // moves to its remove button, which would remove the tag just added.
    const ignoreRepeatedEnter = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' && e.repeat) {
        e.preventDefault()
      }
    }

    const tagJsx = useMemo(() => {
      return tags.map((tag, index) => {
        return (
          <Tag.Root key={index} variant={TagVariant.TagInput as any}>
            <Text fontWeight={500}>{tag}</Text>
            {/* The name holds the email address. Visually hidden text, unlike
                aria-label, is masked by PostHog replay (maskTextSelector: '*'
                in utils/initializePosthog.ts) and not serialized by Sentry. */}
            <CloseButton
              css={styles.tagCloseButton}
              // Overrides CloseButton's default aria-label="Close"
              aria-label={undefined}
              onClick={handleDeleteTag(index)}
              onKeyDown={ignoreRepeatedEnter}
            >
              <X aria-hidden />
              <VisuallyHidden>
                {t('tagInput.removeTag', 'Remove {{value}}', { value: tag })}
              </VisuallyHidden>
            </CloseButton>
          </Tag.Root>
        )
      })
    }, [tags, t])

    const inputDisabled = tags.length >= maxTags
    const showGuideText = !inputErrors && Boolean(guideText)
    const describedBy =
      [showGuideText && guideTextId, inputErrors && errorId]
        .filter(Boolean)
        .join(' ') || undefined

    return (
      <Box as={VStack} css={styles.dataContainer} ref={ref} {...rest}>
        {label && (
          <Text asChild css={styles.label}>
            <label htmlFor={inputId}>{label}</label>
          </Text>
        )}
        <VStack css={styles.inputContainer}>
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <Input
              ref={inputRef}
              id={inputId}
              onBlur={handleFocusLost}
              placeholder={inputDisabled ? undefined : placeholder}
              value={
                inputDisabled
                  ? ''
                  : tryGetValueOrDefault(externalValue, inputValue)
              }
              // Not natively disabled, so it keeps its label and isn't blurred
              // mid-keypress; tabIndex -1 skips it like a disabled field.
              readOnly={inputDisabled}
              aria-disabled={inputDisabled || undefined}
              tabIndex={inputDisabled ? -1 : undefined}
              aria-invalid={inputErrors ? true : undefined}
              aria-describedby={describedBy}
              onChange={handleInputChanged}
            />
          </form>
          {showGuideText && (
            <Text
              id={guideTextId}
              alignSelf="flex-start"
              css={styles.guideText}
            >
              {guideText}
            </Text>
          )}
          {inputErrors && (
            <VStack id={errorId} css={styles.errorContainer}>
              <Text css={styles.errorText}>{getErrorMessage(inputErrors)}</Text>
              {allowMultiple && (
                <Text css={styles.errorText}>
                  {t(
                    'tagInput.commaSeparated',
                    'Multiple values must be comma separated',
                  )}
                </Text>
              )}
            </VStack>
          )}
        </VStack>
        {tags.length > 0 && (
          <HStack ref={tagContainerRef} css={styles.tagContainer}>
            {tagJsx}
          </HStack>
        )}
      </Box>
    )
  },
)
